import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getRank } from "@/lib/ranks";
import { Trophy, Swords, CircleDot, Flame, ArrowRight, Camera, Hand, Users, X, Loader2, MessageCircle } from "lucide-react";
import { LogoHorizontal } from "@/components/Logo";
import { toast } from "sonner";

const STATUS = {
  free: { label: "Trống", color: "#22ee9c" },
  playing: { label: "Đang đấu", color: "#00e5ff" },
  maintenance: { label: "Bảo trì", color: "#ffb020" },
};

export default function TablePublic() {
  const { number } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadQueue = () => api.get(`/tables/${number}/queue`).then(({ data }) => setQueue(data)).catch(() => setQueue([]));

  useEffect(() => {
    const load = () => api.get(`/tables/by-number/${number}`).then(({ data }) => setData(data)).catch(() => setData({ notFound: true }));
    load();
    loadQueue();
    const i = setInterval(() => { load(); loadQueue(); }, 6000);

    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (["queue.join", "queue.leave", "queue.dismiss", "queue.refresh", "match.start", "match.finish"].includes(ev.type)) {
            load();
            loadQueue();
          }
        } catch {}
      };
    } catch {}

    return () => { clearInterval(i); if (ws) ws.close(); };
    // eslint-disable-next-line
  }, [number]);

  const inQueue = queue.find((q) => q.player_id === user?.id);
  const myPos = inQueue ? queue.findIndex((q) => q.player_id === user.id) + 1 : 0;

  const joinQueue = async () => {
    if (!user) {
      toast.info("Vui lòng đăng nhập để vào hàng chờ");
      nav(`/login?next=/table/${number}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tables/${number}/queue/join`);
      toast.success("Đã vào hàng chờ. Nhân viên sẽ sắp xếp trận cho bạn.");
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    } finally {
      setBusy(false);
    }
  };

  const leaveQueue = async () => {
    setBusy(true);
    try {
      await api.delete(`/tables/${number}/queue/leave`);
      toast.success("Đã rời hàng chờ");
      loadQueue();
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen bg-[#05050a] grid place-items-center text-white/40">Đang tải...</div>;
  if (data.notFound) {
    return (
      <div className="min-h-screen bg-[#05050a] grid place-items-center p-6 text-center">
        <div>
          <div className="font-display font-bold text-3xl mb-2">Bàn không tồn tại</div>
          <Link to="/" className="text-cyan-300 underline">Về trang chủ</Link>
        </div>
      </div>
    );
  }

  const t = data.table;
  const s = STATUS[t.status] || STATUS.free;
  const live = data.live_match;

  return (
    <div className="min-h-screen bg-[#05050a]">
      {/* header */}
      <header className="border-b border-white/5 bg-[#070810]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoHorizontal size={32} />
          </Link>
          {user ? (
            <Link to="/" className="text-xs text-cyan-300">Vào app ↗</Link>
          ) : (
            <Link to="/login" className="text-xs text-cyan-300">Đăng nhập</Link>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Hero card */}
        <div className="relative rounded-2xl bg-[#0c0d14] border border-white/8 p-6 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full blur-3xl" style={{ background: `${s.color}22` }} />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Bàn số</div>
              <div className="font-display font-black text-7xl" style={{ color: s.color }} data-testid="table-number">{t.number}</div>
              <div className="font-display font-semibold mt-1">{t.name || `Bàn ${t.number}`}</div>
              <div className="flex items-center gap-2 mt-2">
                <CircleDot size={14} style={{ color: s.color }} className={t.status === "playing" ? "animate-pulse" : ""} />
                <span className="text-sm" style={{ color: s.color }} data-testid="table-status">{s.label}</span>
              </div>
            </div>
          </div>
          {t.camera_url && (
            <a href={t.camera_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-cyan-300 hover:underline">
              <Camera size={14} /> Xem camera trực tiếp
            </a>
          )}
        </div>

        {/* Live match */}
        {live ? (
          <div className="rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/5 border border-cyan-400/30 p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 pulse-ring" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
              </span>
              <div className="text-[10px] uppercase tracking-widest text-cyan-300">Đang diễn ra · BO{live.best_of} · {live.mode}</div>
            </div>
            <div className="grid grid-cols-3 items-center gap-3" data-testid="live-match">
              <div className="flex flex-col items-center text-center">
                <img src={live.player_a_avatar} alt="" className="w-16 h-16 rounded-full border-2" style={{ borderColor: getRank(live.player_a_elo).color }} />
                <div className="font-display font-semibold text-sm mt-2 truncate w-full">{live.player_a_nickname}</div>
                <div className="text-xs" style={{ color: getRank(live.player_a_elo).color }}>{live.player_a_rank} · {live.player_a_elo}</div>
              </div>
              <div className="font-display font-black text-3xl text-white/40 text-center">VS</div>
              <div className="flex flex-col items-center text-center">
                <img src={live.player_b_avatar} alt="" className="w-16 h-16 rounded-full border-2" style={{ borderColor: getRank(live.player_b_elo).color }} />
                <div className="font-display font-semibold text-sm mt-2 truncate w-full">{live.player_b_nickname}</div>
                <div className="text-xs" style={{ color: getRank(live.player_b_elo).color }}>{live.player_b_rank} · {live.player_b_elo}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-6 text-center">
            <Swords size={28} className="mx-auto text-white/30 mb-2" />
            <div className="font-display font-semibold">Chưa có trận đang đấu</div>
            <div className="text-xs text-white/40 mt-1">Hãy đến quầy để được sắp xếp trận!</div>
            {user && (user.role === "admin" || user.role === "staff") && (
              <Link to="/admin/matches/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">
                Tạo trận ở bàn này <ArrowRight size={14} />
              </Link>
            )}
          </div>
        )}

        {/* Queue / Quick Join */}
        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold flex items-center gap-2"><Users size={14} className="text-cyan-300" /> Hàng chờ bàn này <span className="text-white/40 font-normal text-xs">· {queue.length} người</span></h2>
            {inQueue ? (
              <span className="px-2 py-1 rounded-md text-xs bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 font-medium">Bạn ở vị trí #{myPos}</span>
            ) : null}
          </div>

          {queue.length === 0 ? (
            <div className="text-white/40 text-sm py-4 text-center">Chưa có ai trong hàng chờ — bạn có thể là người đầu tiên!</div>
          ) : (
            <div className="space-y-2 mb-4">
              {queue.map((q, i) => (
                <div key={q.id} className={`flex items-center gap-3 p-2.5 rounded-md border ${q.player_id === user?.id ? "bg-cyan-400/10 border-cyan-400/30" : "bg-white/[0.02] border-white/5"}`}>
                  <div className="w-6 text-center font-display font-bold text-sm text-white/60">#{i + 1}</div>
                  <img src={q.player_avatar} alt="" className="w-8 h-8 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{q.player_nickname} {q.player_id === user?.id && <span className="text-cyan-300 text-xs ml-1">(bạn)</span>}</div>
                    <div className="text-[10px]" style={{ color: getRank(q.player_elo).color }}>{q.player_rank} · {q.player_elo}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!user ? (
            <Link to={`/login?next=/table/${number}`} className="block w-full py-3 rounded-md font-display font-semibold uppercase tracking-wider text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black text-center" data-testid="login-to-join-queue">
              Đăng nhập để vào hàng chờ
            </Link>
          ) : inQueue ? (
            <button onClick={leaveQueue} disabled={busy} data-testid="leave-queue-btn"
              className="w-full py-3 rounded-md font-display font-semibold text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Rời hàng chờ
            </button>
          ) : (
            <button onClick={joinQueue} disabled={busy || t.status === "maintenance"} data-testid="join-queue-btn"
              className="w-full py-3 rounded-md font-display font-semibold uppercase tracking-wider text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Hand size={15} />} Tôi muốn chơi bàn này
            </button>
          )}

          <div className="mt-3 text-[11px] text-white/40 text-center">Nhân viên sẽ ghép trận khi có 2 người trong hàng chờ</div>
        </div>

        {/* Recent matches */}
        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-5">
          <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><Flame size={14} className="text-red-400" /> Trận gần đây tại bàn này</h2>
          {data.recent_matches.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Chưa có</div>
          ) : (
            <div className="space-y-2">
              {data.recent_matches.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02] border border-white/5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate"><span className={m.winner_id === m.player_a_id ? "text-cyan-300 font-semibold" : ""}>{m.player_a_nickname}</span> <span className="text-white/30 mx-1">vs</span> <span className={m.winner_id === m.player_b_id ? "text-cyan-300 font-semibold" : ""}>{m.player_b_nickname}</span></div>
                    <div className="text-[10px] text-white/40">BO{m.best_of} · {m.mode}</div>
                  </div>
                  <div className="font-display font-bold text-lg">{m.score_a}-{m.score_b}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center pt-2 pb-8">
          {user ? (
            <Link to="/leaderboard" className="text-sm text-cyan-300 hover:underline">Xem bảng xếp hạng →</Link>
          ) : (
            <Link to="/register" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-gradient-to-r from-cyan-400 to-purple-500 text-black font-display font-semibold text-sm">
              Tạo tài khoản · Bắt đầu leo rank <ArrowRight size={15} />
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
